import { useEffect, useRef } from "react";
import axios from "axios";
import { getCable } from "../cable";
import { useAuth } from "../contexts/AuthContext";

const apiUrl = process.env.REACT_APP_API_URL;

export const fetchConversations = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/api/v1/conversations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchMessages = async (conversationId, token) => {
  try {
    const response = await axios.get(
      `${apiUrl}/api/v1/conversations/${conversationId}/messages`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const markMessagesAsRead = async (conversationId, token) => {
  try {
    await axios.patch(
      `${apiUrl}/api/v1/conversations/${conversationId}/messages/mark_read`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const sendMessage = async (
  conversationId,
  message,
  token,
  file = null,
) => {
  try {
    const formData = new FormData();
    formData.append("message[body]", message);
    if (file) formData.append("message[attachment]", file);

    await axios.post(
      `${apiUrl}/api/v1/conversations/${conversationId}/messages`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}min ago`;
  if (diffHours < 24) return `${diffHours}hr ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const formatMessageTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) return `Today at ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${time}`;
  }

  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${time}`;
};

export const getDateDivider = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export const groupMessagesByDate = (messages, getDateDivider) => {
  return messages.reduce((groups, message) => {
    const date = getDateDivider(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});
};

export const getSelectedUserProfile = (selectedConversation, currentUser) => {
  if (!selectedConversation) return null;
  return selectedConversation.sender?.id === currentUser?.id
    ? selectedConversation.recipient
    : selectedConversation.sender;
};

export const filterConversations = (
  conversations,
  searchTerm,
  activeFilter,
  internalNotifications,
  user,
) => {
  let filtered = conversations;

  if (searchTerm && searchTerm.trim() !== "") {
    filtered = filtered.filter((conv) => {
      const isSender = conv.sender?.id === user?.id;
      const displayUser = isSender ? conv.recipient : conv.sender;
      if (!displayUser) return false;
      const conversationName =
        `${displayUser.first_name || ""} ${displayUser.last_name || ""}`
          .toLowerCase()
          .trim();
      return conversationName.includes(searchTerm.toLowerCase().trim());
    });
  }

  if (activeFilter !== "all") {
    const filterValue = activeFilter.toLowerCase();
    filtered = filtered.filter((conv) => {
      const unreadCount =
        internalNotifications?.byConversation?.[String(conv.id)] || 0;
      if (filterValue === "unread") return unreadCount > 0;
      if (filterValue === "read") return unreadCount === 0;
      return true;
    });
  }

  filtered.sort((a, b) => {
    const dateA = a.last_message?.created_at
      ? new Date(a.last_message.created_at)
      : new Date(0);
    const dateB = b.last_message?.created_at
      ? new Date(b.last_message.created_at)
      : new Date(0);
    return dateB - dateA;
  });

  return filtered;
};

export const showToast = (setToasts, message, conversationId = null) => {
  const id = Date.now();
  setToasts((prev) => {
    const newToasts = [...prev, { id, message, conversationId }];
    return newToasts.length > 3 ? newToasts.slice(-3) : newToasts;
  });
  setTimeout(() => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, 4000);
  return id;
};

const playNotificationSound = () => {
  try {
    const audio = new Audio(
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=",
    );
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (e) {}
};

export const useConversationWebSocket = (
  selectedConversation,
  user,
  onNewMessage,
  setIsTyping,
) => {
  const { token } = useAuth();
  const subscriptionRef = useRef(null);

  const conversationId = selectedConversation?.id;
  const userId = user?.id;

  useEffect(() => {
    if (!conversationId || !token || !userId) return;

    const cable = getCable(token);
    if (!cable) return;

    const subscription = cable.subscriptions.create(
      { channel: "ConversationChannel", conversation_id: conversationId },
      {
        received: (data) => {
          if (data.type === "typing") {
            setIsTyping(data.is_typing || false);
            return;
          }

          const messageBody = data.body || data.message || data.text;
          if (messageBody || data.attachment) {
            const message = {
              id: data.id || data.message_id,
              body: messageBody,
              sender_id: data.user_id || data.sender_id,
              created_at: data.created_at || new Date().toISOString(),
              attachment: data.attachment || null,
              conversation_id: conversationId,
            };

            onNewMessage(message);
            setIsTyping(false);
          }
        },
      },
    );

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        } catch (error) {}
      }
    };
  }, [conversationId, userId, token, onNewMessage, setIsTyping]);
};

export const useNotificationWebSocket = (
  token,
  selectedConversation,
  conversations,
  fetchConversations,
  showToast,
  currentUser,
  updateConversationPreview,
) => {
  const subscriptionRef = useRef(null);
  const latestRef = useRef({});
  const processedToastIds = useRef(new Set());

  latestRef.current = {
    selectedConversation,
    conversations,
    fetchConversations,
    showToast,
    currentUser,
    updateConversationPreview,
  };

  useEffect(() => {
    if (!token) return;

    const cable = getCable(token);
    if (!cable) return;

    const subscription = cable.subscriptions.create(
      { channel: "InternalNotificationsChannel" },
      {
        received: (data) => {
          if (!data.notifiable) return;

          const toastId =
            data.notification_id ||
            data.id ||
            `${data.notifiable.conversation_id}-${data.timestamp || Date.now()}`;

          if (processedToastIds.current.has(toastId)) return;
          processedToastIds.current.add(toastId);

          setTimeout(() => {
            processedToastIds.current.delete(toastId);
          }, 5000);

          const {
            selectedConversation: selConv,
            fetchConversations: reload,
            showToast: toast,
            updateConversationPreview: updatePreview,
          } = latestRef.current;

          if (data.notifiable.conversation_id) {
            const convId = data.notifiable.conversation_id;

            if (selConv && Number(selConv.id) === Number(convId)) return;

            const messageData = data.message_data ||
              data.message ||
              data.notifiable.message_data || { body: data.notifiable.message };

            const previewMessage = {
              id: data.message_id || Date.now(),
              body: messageData.body || messageData,
              created_at: data.created_at || new Date().toISOString(),
              attachment: messageData.attachment || null,
            };

            updatePreview(convId, previewMessage);
            reload();
            playNotificationSound();

            let notificationMessage = messageData.body || messageData;

            if (notificationMessage === "New message" || !notificationMessage) {
              const senderName =
                data.sender_name || data.notifiable.sender_name;
              if (senderName) {
                notificationMessage = `New message from ${senderName}`;
              } else {
                return;
              }
            }

            if (typeof toast === "function") {
              toast(notificationMessage, convId, toastId);
            }

            try {
              window.dispatchEvent(
                new CustomEvent("notification:show-toast", {
                  detail: {
                    message: notificationMessage,
                    conversationId: convId,
                    toastId: toastId,
                  },
                }),
              );
            } catch (e) {}
          } else if (data.notifiable.appointment_id) {
            playNotificationSound();
            const { showToast: t } = latestRef.current;
            if (typeof t === "function") {
              t(
                `Appointment ${data.notifiable.status || "updated"}`,
                null,
                toastId,
              );
            }
          }
        },
      },
    );

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
          cable.subscriptions.remove(subscriptionRef.current);
          subscriptionRef.current = null;
        } catch (error) {}
      }
    };
  }, [token]);
};

const fetchConversationsData = {
  fetchConversations,
  fetchMessages,
  markMessagesAsRead,
  sendMessage,
  formatTime,
  formatMessageTime,
  getDateDivider,
  groupMessagesByDate,
  getSelectedUserProfile,
  filterConversations,
  showToast,
  useConversationWebSocket,
  useNotificationWebSocket,
};

export default fetchConversationsData;
